import { Link } from "wouter";
import { Phone, Mail, MapPin } from "lucide-react";
import { useEffect, useRef } from "react";
import { trackPhoneClick } from "@/lib/analytics";

const messagingServices = [
  { name: "Bulk SMS", href: "/bulk-sms" },
  { name: "WhatsApp Business API", href: "/whatsapp-business-api" },
  { name: "Voice Call Service", href: "/voice-call" },
  { name: "RCS Messaging", href: "/rcs-messaging" },
  { name: "Email Marketing", href: "/email-marketing" },
  { name: "DLT Registration", href: "/dlt-registration" },
  { name: "OTP SMS", href: "/otp-sms" },
  { name: "Transactional SMS", href: "/transactional-sms" },
  { name: "Promotional SMS", href: "/promotional-sms" },
  { name: "International Bulk SMS", href: "/international-bulk-sms" },
  { name: "SMS Reseller Program", href: "/reseller-program" },
];

const marketingServices = [
  { name: "Google Ads", href: "/google-ads-services" },
  { name: "Meta Ads", href: "/meta-ads" },
  { name: "SEO Services", href: "/seo-services" },
  { name: "Local SEO", href: "/local-seo" },
  { name: "Social Media Marketing", href: "/social-media-marketing" },
  { name: "Social Media Monetization", href: "/social-media-monetization" },
  { name: "Website Design", href: "/website-design" },
  { name: "Lead Generation", href: "/lead-generation" },
  { name: "Content Marketing", href: "/content-marketing" },
];

const companyLinks = [
  { name: "About Us", href: "/about" },
  { name: "Industries", href: "/industries" },
  { name: "Case Studies", href: "/case-studies" },
  { name: "How We Work", href: "/testimonials" },
  { name: "Pricing", href: "/pricing" },
  { name: "Blog", href: "/blog" },
  { name: "FAQ", href: "/faq" },
  { name: "Resources", href: "/resources" },
  { name: "Write for Us", href: "/write-for-us" },
  { name: "Contact Us", href: "/contact" },
];

const toolLinks = [
  { name: "AI Template Generator", href: "/tools/template-generator" },
  { name: "DLT Template Validator", href: "/tools/dlt-validator" },
  { name: "WhatsApp Number Scrubber", href: "/tools/whatsapp-number-scrubber" },
  { name: "SMS Character Counter", href: "/tools/sms-character-counter" },
  { name: "Free SEO Checker", href: "/tools/seo-checker" },
  { name: "DLT Compliance Guide", href: "/dlt-guide" },
  { name: "SMS Statistics India", href: "/sms-marketing-statistics-india" },
];

const legalLinks = [
  { name: "Privacy Policy", href: "/privacy-policy" },
  { name: "Terms & Conditions", href: "/terms-conditions" },
  { name: "Refund Policy", href: "/refund-policy" },
  { name: "Cookie Policy", href: "/cookie-policy" },
  { name: "Disclaimer", href: "/disclaimer" },
];

const locationLinks = [
  { name: "Bulk SMS in Mumbai", href: "/bulk-sms" },
  { name: "Website Development in Thane", href: "/website-design" },
  { name: "Business Messaging in Bhayandar East", href: "/bulk-sms" },
  { name: "Bulk SMS in Mira Road", href: "/bulk-sms" },
  { name: "Digital Marketing in Navi Mumbai", href: "/seo-services" },
  { name: "Website Development in Vasai-Virar", href: "/website-design" },
];

// Floating SMS/Chat bubble particles component
function FloatingBubbles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    let width = canvas.offsetWidth;
    let height = canvas.offsetHeight;
    canvas.width = width;
    canvas.height = height;

    const bubbles: Array<{
      x: number;
      y: number;
      size: number;
      speedY: number;
      speedX: number;
      opacity: number;
      type: "sms" | "chat" | "envelope" | "phone";
      rotation: number;
      rotationSpeed: number;
    }> = [];

    // Create bubbles
    for (let i = 0; i < 18; i++) {
      bubbles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        size: 14 + Math.random() * 18,
        speedY: -(0.3 + Math.random() * 0.6),
        speedX: (Math.random() - 0.5) * 0.4,
        opacity: 0.08 + Math.random() * 0.15,
        type: (["sms", "chat", "envelope", "phone"] as const)[Math.floor(Math.random() * 4)],
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.01,
      });
    }

    function drawSMSBubble(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, opacity: number, rotation: number) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.globalAlpha = opacity;
      // Chat bubble shape
      ctx.beginPath();
      const r = size * 0.3;
      ctx.roundRect(-size / 2, -size / 2.5, size, size * 0.7, r);
      ctx.fillStyle = "#FF6B2C";
      ctx.fill();
      // Tail
      ctx.beginPath();
      ctx.moveTo(-size * 0.1, size * 0.2);
      ctx.lineTo(-size * 0.3, size * 0.45);
      ctx.lineTo(size * 0.1, size * 0.2);
      ctx.fillStyle = "#FF6B2C";
      ctx.fill();
      // Lines inside
      ctx.fillStyle = "rgba(255,255,255,0.7)";
      ctx.fillRect(-size * 0.3, -size * 0.15, size * 0.6, 2);
      ctx.fillRect(-size * 0.3, -size * 0.02, size * 0.4, 2);
      ctx.restore();
    }

    function drawChatBubble(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, opacity: number, rotation: number) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.globalAlpha = opacity;
      // Round chat bubble
      ctx.beginPath();
      ctx.arc(0, 0, size * 0.4, 0, Math.PI * 2);
      ctx.fillStyle = "#4F9CF7";
      ctx.fill();
      // Three dots
      ctx.fillStyle = "rgba(255,255,255,0.8)";
      ctx.beginPath(); ctx.arc(-size * 0.12, 0, 2, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(0, 0, 2, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(size * 0.12, 0, 2, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }

    function drawEnvelope(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, opacity: number, rotation: number) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.globalAlpha = opacity;
      // Envelope body
      ctx.fillStyle = "#10B981";
      ctx.fillRect(-size * 0.4, -size * 0.25, size * 0.8, size * 0.5);
      // Flap
      ctx.beginPath();
      ctx.moveTo(-size * 0.4, -size * 0.25);
      ctx.lineTo(0, size * 0.05);
      ctx.lineTo(size * 0.4, -size * 0.25);
      ctx.fillStyle = "#059669";
      ctx.fill();
      ctx.restore();
    }

    function drawPhone(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, opacity: number, rotation: number) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rotation);
      ctx.globalAlpha = opacity;
      // Phone body
      ctx.beginPath();
      ctx.roundRect(-size * 0.2, -size * 0.35, size * 0.4, size * 0.7, 4);
      ctx.fillStyle = "#8B5CF6";
      ctx.fill();
      // Screen
      ctx.fillStyle = "rgba(255,255,255,0.3)";
      ctx.fillRect(-size * 0.14, -size * 0.25, size * 0.28, size * 0.45);
      ctx.restore();
    }

    function animate() {
      ctx!.clearRect(0, 0, width, height);
      for (const bubble of bubbles) {
        bubble.y += bubble.speedY;
        bubble.x += bubble.speedX;
        bubble.rotation += bubble.rotationSpeed;

        // Wrap around
        if (bubble.y < -bubble.size) bubble.y = height + bubble.size;
        if (bubble.x < -bubble.size) bubble.x = width + bubble.size;
        if (bubble.x > width + bubble.size) bubble.x = -bubble.size;

        switch (bubble.type) {
          case "sms": drawSMSBubble(ctx!, bubble.x, bubble.y, bubble.size, bubble.opacity, bubble.rotation); break;
          case "chat": drawChatBubble(ctx!, bubble.x, bubble.y, bubble.size, bubble.opacity, bubble.rotation); break;
          case "envelope": drawEnvelope(ctx!, bubble.x, bubble.y, bubble.size, bubble.opacity, bubble.rotation); break;
          case "phone": drawPhone(ctx!, bubble.x, bubble.y, bubble.size, bubble.opacity, bubble.rotation); break;
        }
      }
      // Render once only. The footer decoration is not an interactive task and
      // does not need to consume a frame on every animation tick.
      animationId = 0;
    }

    animate();

    const handleResize = () => {
      width = canvas.offsetWidth;
      height = canvas.offsetHeight;
      canvas.width = width;
      canvas.height = height;
      animate();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none z-0"
      aria-hidden="true"
      // Explicit dimensions prevent layout reflow when canvas initialises in useEffect
      width={1}
      height={1}
    />
  );
}

export default function Footer() {
  return (
    <footer className="bg-navy text-white relative overflow-hidden" style={{ contain: 'layout' }}>
      {/* Animated floating SMS/Chat bubbles */}
      <FloatingBubbles />
      <div className="absolute inset-0 rangoli-pattern opacity-10" />

      {/* Main Footer */}
      <div className="container section-padding relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-5">
              <img
                src="/assets/bharat_bulk_sms_logo_65px_4a34d862_c8404b73.webp"
                alt="Bharat Bulk SMS Logo"
                className="h-12 w-auto max-w-[150px] object-contain"
                width={150}
                height={65}
                loading="eager"
                fetchPriority="low"
                decoding="async"
                style={{
                  filter: 'drop-shadow(0 4px 8px rgba(255,107,44,0.35)) drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                }}
              />
            </div>
            <p className="text-white/90 text-sm leading-relaxed mb-6 max-w-sm">
              Bulk messaging and digital marketing services for Indian businesses, with SMS, WhatsApp, voice and campaign-support options.
            </p>
            <div className="max-w-md space-y-3">
              <a
                href="tel:+917208417941"
                onClick={() => trackPhoneClick("+917208417941")}
                aria-label="Call Bharat Bulk SMS at +91 7208417941"
                className="group flex min-h-[56px] items-center gap-3 rounded-xl bg-saffron px-4 py-3 text-white shadow-lg shadow-saffron/20 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#ff7d44] hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/15">
                  <Phone className="h-4 w-4" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-white/80">Call Bharat Bulk SMS</span>
                  <span className="block text-base font-extrabold tracking-wide">+91 7208417941</span>
                </span>
              </a>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Ostwal%20Shopping%20Centre%2C%20Opposite%20Bhayandar%20East%20Station%2C%20Jesal%20Park%2C%20Bhayandar%20East%2C%20Thane%20401105"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open Bharat Bulk SMS Bhayandar East office address in Google Maps"
                className="group flex min-h-[76px] items-start gap-3 rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm text-white/90 transition-colors hover:border-saffron/70 hover:bg-white/[0.09] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron"
              >
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-saffron" aria-hidden="true" />
                <span>
                  <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.16em] text-saffron">Bhayandar East Office</span>
                  <span className="block leading-relaxed">Ostwal Shopping Centre, Opposite Bhayandar East Station, Jesal Park, Bhayandar East, Thane – 401105</span>
                  <span className="mt-1 block text-xs text-white/65 group-hover:text-white/80">Open in Google Maps</span>
                </span>
              </a>
              <a href="mailto:support@bharatbulksms.com" className="flex min-h-[44px] items-center gap-3 px-1 text-sm text-white/80 transition-colors hover:text-saffron focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron">
                <Mail className="h-4 w-4 text-saffron" aria-hidden="true" />
                support@bharatbulksms.com
              </a>
            </div>
          </div>

          {/* Messaging Services */}
          <div>
            <h3 className="font-bold text-sm uppercase tracking-wider mb-5 text-saffron">
              Messaging
            </h3>
            <ul className="space-y-1">
              {messagingServices.map((link) => (
                <li key={link.name}>
                  <Link href={link.href} className="text-sm text-white/90 hover:text-white transition-colors block py-2 min-h-[44px] flex items-center">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Marketing Services */}
          <div>
            <h3 className="font-bold text-sm uppercase tracking-wider mb-5 text-saffron">
              Marketing
            </h3>
            <ul className="space-y-1">
              {marketingServices.map((link) => (
                <li key={link.name}>
                  <Link href={link.href} className="text-sm text-white/90 hover:text-white transition-colors block py-2 min-h-[44px] flex items-center">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-bold text-sm uppercase tracking-wider mb-5 text-saffron">
              Company
            </h3>
            <ul className="space-y-1">
              {companyLinks.map((link) => (
                <li key={link.name}>
                  <Link href={link.href} className="text-sm text-white/90 hover:text-white transition-colors block py-2 min-h-[44px] flex items-center">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h3 className="font-bold text-sm uppercase tracking-wider mt-8 mb-4 text-saffron">
              Free Tools
            </h3>
            <ul className="space-y-1 mb-6">
              {toolLinks.map((link) => (
                <li key={link.name}>
                  <Link href={link.href} className="text-sm text-white/90 hover:text-white transition-colors block py-2 min-h-[44px] flex items-center">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h3 className="font-bold text-sm uppercase tracking-wider mt-8 mb-4 text-saffron">
              Legal
            </h3>
            <ul className="space-y-1">
              {legalLinks.map((link) => (
                <li key={link.name}>
                  <Link href={link.href} className="text-sm text-white/90 hover:text-white transition-colors block py-2 min-h-[44px] flex items-center">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Local discovery links point to relevant canonical service pages,
            rather than creating thin location-only landing pages. */}
        <div className="mt-12 border-t border-white/10 pt-8">
          <h3 className="font-bold text-sm uppercase tracking-wider mb-4 text-saffron">
            Locations We Serve
          </h3>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {locationLinks.map((link) => (
              <Link
                key={link.name}
                href={link.href}
                className="text-sm text-white/75 hover:text-white transition-colors underline-offset-4 hover:underline"
              >
                {link.name}
              </Link>
            ))}
          </div>
          <p className="mt-4 max-w-3xl text-xs leading-relaxed text-white/55">
            Serving businesses across Mumbai, Thane and nearby Maharashtra markets with bulk SMS, website development and digital marketing support from our Bhayandar East office.
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 relative z-10">
        {/* pb-28 on mobile for StickyMobileCta + floating buttons, pb-20 on desktop for floating buttons */}
        <div className="container py-6 pb-28 md:pb-20 flex flex-col items-center gap-3">
          <p className="text-sm text-white/80 text-center">
            &copy; {new Date().getFullYear()} Bharat Bulk SMS. All rights reserved.
          </p>
          <p className="text-sm text-white/80 text-center">
            Powered by <a href="https://www.gooadvert.com" target="_blank" rel="noopener noreferrer" className="hover:text-saffron transition-colors font-semibold">GOO ADVERT</a>
          </p>
        </div>
      </div>
    </footer>
  );
}
